"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { ArrowRight, X, Sparkles, ArrowDown, Circle } from "lucide-react"

export default function LandingPage() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    antallBrukere: "",
    navarendeLeverandor: "",
    manedligKostnad: "",
    navn: "",
    bedrift: "",
    telefonnummer: "+47",
    epost: "",
    tilleggsinformasjon: "",
  })
  const [formErrors, setFormErrors] = useState({
    antallBrukere: '',
    navarendeLeverandor: '',
    manedligKostnad: '',
    navn: '',
    bedrift: '',
    telefonnummer: '',
    epost: '',
  })
  const [errorPopup, setErrorPopup] = useState<string[] | null>(null)
  const [resultPopup, setResultPopup] = useState<{ success: boolean; message: string } | null>(null)

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setFormErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const handleNext = () => {
    let errors: any = {}
    if (!formData.antallBrukere) errors.antallBrukere = 'Antall brukere'
    if (!formData.navarendeLeverandor) errors.navarendeLeverandor = 'Nåværende leverandør'
    if (!formData.manedligKostnad) errors.manedligKostnad = 'Månedlig kostnad'
    setFormErrors((prev) => ({ ...prev, ...errors }))
    if (Object.keys(errors).length === 0) {
    setCurrentStep(2)
    } else {
      setErrorPopup(Object.values(errors))
    }
  }

  const handleBack = () => {
    setCurrentStep(1)
  }

  const handleSubmit = async () => {
    let errors: any = {}
    if (!formData.navn) errors.navn = 'Navn'
    if (!formData.bedrift) errors.bedrift = 'Bedrift'
    if (!formData.telefonnummer || formData.telefonnummer.trim() === '+47') errors.telefonnummer = 'Telefonnummer'
    if (!formData.epost) errors.epost = 'E-post'
    setFormErrors((prev) => ({ ...prev, ...errors }))
    if (Object.keys(errors).length === 0) {
      // Send form data to PHP script
      try {
        const res = await fetch('/send-form.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(formData as any).toString(),
        })
        const data = await res.json()
        if (data.success) {
          setResultPopup({
            success: true,
            message: 'Din forespørsel er sendt, du blir kontaktet i løpet av 24 timer(virkedager)',
          })
          setTimeout(() => {
            setResultPopup(null)
            setIsModalOpen(false)
            setCurrentStep(1)
          }, 5000)
        } else {
          setResultPopup({
            success: false,
            message: 'Forespørselen din ble ikke sendt, vennligst prøv på nytt senere.',
          })
          setTimeout(() => {
            setResultPopup(null)
            setIsModalOpen(false)
            setCurrentStep(1)
          }, 5000)
        }
      } catch (e) {
        setResultPopup({
          success: false,
          message: 'Forespørselen din ble ikke sendt, vennligst prøv på nytt senere.',
        })
        setTimeout(() => {
          setResultPopup(null)
    setIsModalOpen(false)
    setCurrentStep(1)
        }, 5000)
      }
    }
  }

  const openModal = () => {
    setIsModalOpen(true)
    setCurrentStep(1)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setCurrentStep(1)
  }

  return (
    <div className="min-h-screen bg-amber-50">
      {/* Side Navigation */}
      <nav className="fixed left-8 top-1/2 transform -translate-y-1/2 z-50 flex flex-col items-center space-y-8">
        {/* Logo Section */}
        <div className="flex flex-col items-center space-y-6">
          <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>

          <div className="flex flex-col items-center space-y-1">
            <div className="text-xs font-black text-gray-900 tracking-[0.2em]">BEDRIFTS</div>
            <div className="text-xs font-black text-orange-800 tracking-[0.2em]">ABONNEMENT</div>
            <div className="text-xs font-black text-gray-900 tracking-[0.2em]">24</div>
          </div>

          <div className="w-1 h-12 bg-gradient-to-b from-orange-400 to-red-400 rounded-full"></div>
        </div>

        {/* CTA Button */}
        <Button
          onClick={openModal}
          className="-rotate-90 origin-center bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-xs px-6 py-2 font-bold tracking-wide"
        >
          START NÅ
        </Button>
      </nav>

      {/* Main Content Area */}
      <div className="pl-24">
        {/* Hero - Magazine Style */}
        <section className="min-h-screen flex items-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-bl from-orange-100 to-red-100 transform skew-x-12 origin-top-right"></div>

          <div className="max-w-6xl mx-auto px-8 py-20 relative">
            <div className="grid grid-cols-12 gap-8 items-center">
              <div className="col-span-8 space-y-12">
                <div className="space-y-6">
                  <div className="text-xs font-bold text-orange-800 tracking-[0.3em] uppercase">
                    Mobilkostnad Revolusjon
                  </div>

                  <h1 className="text-8xl font-black leading-none">
                    <div className="text-gray-900">KUTT</div>
                    <div className="text-orange-800 -mt-4">MOBILKOSTNADENE</div>
                    <div className="text-gray-900 -mt-4 text-6xl">FOR BEDRIFTEN</div>
                    <div className="text-gray-900 -mt-4 text-4xl italic font-light">— ENKELT OG GRATIS</div>
                  </h1>

                  <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-red-400"></div>
                </div>

                <div className="grid grid-cols-2 gap-12">
                  <div>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      Vi hjelper norske bedrifter å spare tusenvis på mobilabonnement — helt gratis. Vi samler tilbud fra alle store leverandører, og gir deg det beste. Ingen kostnad. 100 % nøytralt.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <span aria-label="check" className="text-green-500"><svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg></span>
                      <span className="text-sm text-gray-600"><span className="font-bold text-orange-800">800+</span> bedrifter har spart med vår hjelp</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span aria-label="check" className="text-green-500"><svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg></span>
                      <span className="text-sm text-gray-600">Over <span className="font-bold text-orange-800">2 millioner</span> kroner spart i 2024</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span aria-label="check" className="text-green-500"><svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg></span>
                      <span className="text-sm text-gray-600">Gjennomsnittlig besparelse: <span className="font-bold text-orange-800">42%</span></span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={openModal}
                  className="bg-gray-900 hover:bg-gray-800 text-white px-12 py-6 text-lg font-bold tracking-wide"
                >
                  Få gratis tilbud nå
                  <ArrowRight className="ml-4 w-5 h-5" />
                </Button>
              </div>

              <div className="col-span-4 relative">
                <div className="absolute -top-20 -right-20 w-80 h-80 bg-gradient-to-br from-orange-200 to-red-200 rounded-full opacity-20"></div>
                <div className="relative bg-white p-8 shadow-2xl transform -rotate-6 hover:rotate-0 transition-transform duration-700">
                  <div className="text-center space-y-6">
                    <div className="text-5xl font-black text-orange-800">42%</div>
                    <div className="text-sm text-gray-600 uppercase tracking-wide">Snitt besparelse</div>
                    <div className="w-full h-px bg-gray-200"></div>
                    <div className="text-xs text-gray-500">Basert på 800+ bedrifter</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <ArrowDown className="w-6 h-6 text-orange-400" />
          </div>
        </section>

        {/* Diagonal Section */}
        <section className="relative py-32 bg-gradient-to-br from-gray-900 to-black text-white overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-32 bg-amber-50 transform -skew-y-2 origin-top-left"></div>
          <div className="absolute bottom-0 right-0 w-full h-32 bg-orange-50 transform skew-y-2 origin-bottom-right"></div>

          <div className="max-w-6xl mx-auto px-8 relative">
            <div className="text-center mb-20">
              <div className="text-xs font-bold text-orange-800 tracking-[0.3em] uppercase mb-4">Hvordan vi hjelper deg å spare — uten at du løfter en finger</div>
              <h2 className="text-6xl font-black mb-8">
                <span className="text-white">Enkel prosess.</span>
                <br />
                <span className="text-orange-800">Store besparelser.</span>
              </h2>
            </div>

            <div className="grid grid-cols-3 gap-16">
              <div className="text-center group">
                <div className="w-24 h-24 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl font-black text-white">01</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">Vi henter inn tilbud</h3>
                <p className="text-gray-300 leading-relaxed">
                  <span className="block bg-white/90 text-gray-900 rounded-lg px-4 py-2 shadow-md">
                    Vi kontakter alle relevante mobiloperatører på vegne av din bedrift. De vet at de må gi sitt beste tilbud — ellers vinner en konkurrent.
                  </span>
                </p>
              </div>

              <div className="text-center group">
                <div className="w-24 h-24 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl font-black text-white">02</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">Du får beste pris</h3>
                <p className="text-gray-300 leading-relaxed">
                  <span className="block bg-white/90 text-gray-900 rounded-lg px-4 py-2 shadow-md">
                    Vi sammenligner alle tilbudene finner den som gir deg mest verdi for pengene. Helt gratis og uforpliktende.
                  </span>
                </p>
              </div>

              <div className="text-center group">
                <div className="w-24 h-24 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl font-black text-white">03</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">Enkel oppstart</h3>
                <p className="text-gray-300 leading-relaxed">
                  <span className="block bg-white/90 text-gray-900 rounded-lg px-4 py-2 shadow-md">
                    Du bestemmer. Vi setter deg i kontakt med leverandøren som har gitt det beste tilbudet, så kan du høre mer — helt uforpliktende.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Split Screen Section */}
        <section className="min-h-screen flex">
          <div className="w-1/2 bg-orange-50 flex items-center justify-center p-16">
            <div className="max-w-md">
              <div className="text-xs font-bold text-orange-800 tracking-[0.3em] uppercase mb-4">Resultater som teller</div>
              <h2 className="text-5xl font-black text-gray-900 mb-8">
                Resultater som teller
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Våre kunder sparer i snitt 42 % på mobilkostnadene. La oss vise hva vi kan gjøre for din bedrift — helt gratis og uforpliktende.
              </p>
              <Button
                onClick={openModal}
                className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 font-bold tracking-wide"
              >
                Start og spar penger i dag
              </Button>
            </div>
          </div>

          <div className="w-1/2 bg-gray-900 flex items-center justify-center p-16">
            <div className="space-y-8 w-full max-w-md">
              <div className="text-orange-200 text-xs font-bold uppercase mb-4 tracking-wide">Eksempler fra faktiske kunder i 2024</div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-white font-bold">Elin A., Daglig leder</div>
                    <div className="text-gray-400 text-sm">16 ansatte</div>
                  </div>
                  <div className="text-3xl font-black text-green-500">-41%</div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-white font-bold">Adam S., Prosjectleder</div>
                    <div className="text-gray-400 text-sm">8 ansatte</div>
                  </div>
                  <div className="text-3xl font-black text-green-500">-36%</div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-white font-bold">Jon B., Daglig leder</div>
                    <div className="text-gray-400 text-sm">72 ansatte</div>
                  </div>
                  <div className="text-3xl font-black text-green-500">-46%</div>
                </div>
              </div>

              <div className="text-center pt-8">
                <div className="text-4xl font-black text-white mb-2 flex items-center justify-center gap-2"><span role='img' aria-label='briefcase'>💼</span>Over 2 millioner kroner spart i 2024</div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA - Creative Layout */}
        <section className="py-32 bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="max-w-4xl mx-auto px-8 text-center relative">
            <div className="space-y-12">
              <div>
                <div className="text-xs font-bold text-orange-800 tracking-[0.3em] uppercase mb-4">
                  Klar til å spare penger?
                </div>
                <h2 className="text-7xl font-black mb-8">
                  <span className="text-white">La oss kutte mobilkostnadene for bedriften din</span>
                  <br />
                  <span className="text-orange-800">— gratis og enkelt</span>
                </h2>
              </div>

              <div className="flex flex-col md:flex-row justify-center gap-6 text-white/90">
                <div className="flex flex-col items-center justify-center px-6 py-4 rounded-2xl bg-white/10 border border-white/20 shadow-sm w-full md:w-auto">
                  <div className="flex items-center gap-2 mb-1">
                    <span aria-label="check" className="text-green-400 text-3xl">✅</span>
                    <span className="text-4xl font-black text-green-400">800+</span>
                  </div>
                  <span className="text-base font-semibold text-white/90 text-center">bedrifter har brukt tjenesten</span>
                </div>
                <div className="flex flex-col items-center justify-center px-6 py-4 rounded-2xl bg-white/10 border border-white/20 shadow-sm w-full md:w-auto">
                  <div className="flex items-center gap-2 mb-1">
                    <span aria-label="clock" className="text-blue-200 text-3xl">⏱</span>
                    <span className="text-4xl font-black text-blue-200">24t</span>
                  </div>
                  <span className="text-base font-semibold text-white/90 text-center">responstid</span>
                </div>
                <div className="flex flex-col items-center justify-center px-6 py-4 rounded-2xl bg-white/10 border border-white/20 shadow-sm w-full md:w-auto">
                  <div className="flex items-center gap-2 mb-1">
                    <span aria-label="money" className="text-green-300 text-3xl">💸</span>
                    <span className="text-4xl font-black text-green-300">42%</span>
                  </div>
                  <span className="text-base font-semibold text-white/90 text-center">gjennomsnittlig besparelse</span>
                </div>
              </div>

              <Button
                onClick={openModal}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white w-full md:w-auto text-xl font-black py-5 rounded-xl mt-8"
              >
                Få gratis tilbud nå
              </Button>

              <div className="text-white/90 text-lg font-semibold text-center mt-6">Gratis tjeneste! • Ingen forpliktelser • Du sparer, vi gjør jobben</div>
            </div>
          </div>
        </section>

        {/* Minimal Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-6xl mx-auto px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6 md:gap-0">
              <div>
                <div className="text-2xl font-black uppercase tracking-wide">BEDRIFTSABONNEMENT24.NO</div>
                <div className="text-gray-400 text-sm mt-2">Vi hjelper norske bedrifter å kutte mobilkostnader — enkelt, gratis og uforpliktende.</div>
              </div>
              <div className="text-gray-400 text-sm text-center md:text-right">
                <div>© 2024 Bedriftsabonnement24.no. Alle rettigheter reservert.</div>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md bg-gradient-to-br from-orange-50 to-red-50 border-0 rounded-3xl">
          <DialogHeader className="relative text-center">
            <button
              onClick={closeModal}
              className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-white shadow-xl flex items-center justify-center hover:bg-gray-50 transition-colors border border-orange-100"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
            <DialogTitle className="text-3xl font-black text-gray-900">La oss finne beste pris for din bedrift — <span className="text-orange-800">gratis</span></DialogTitle>
            <p className="text-gray-600 mt-2">Vi sammenligner tilbud fra flere operatører. Du bestemmer selv om du vil takke ja.</p>
          </DialogHeader>

          {currentStep === 1 && (
            <div className="space-y-3 pt-2">
              <div className="space-y-1">
                <Label htmlFor="antallBrukere" className="text-sm font-bold text-gray-700">
                  Antall brukere <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="antallBrukere"
                  placeholder="f.eks 5"
                  value={formData.antallBrukere}
                  onChange={(e) => handleInputChange("antallBrukere", e.target.value)}
                  className="bg-white border-orange-200 rounded-xl py-2 focus:border-orange-400 focus:ring-orange-400"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="navarendeLeverandor" className="text-sm font-bold text-gray-700">
                  Nåværende leverandør <span className="text-red-500">*</span>
                </Label>
                <Select
                  onValueChange={(value) => handleInputChange("navarendeLeverandor", value)}
                  value={formData.navarendeLeverandor}
                >
                  <SelectTrigger
                    id="navarendeLeverandor"
                    className="w-full bg-white border-orange-200 rounded-xl py-2 text-left focus:border-orange-400 focus:ring-orange-400"
                  >
                    <SelectValue placeholder="Velg din nåværende leverandør" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Unifon">Unifon</SelectItem>
                    <SelectItem value="Talkmore">Talkmore</SelectItem>
                    <SelectItem value="ChilliMobil">ChilliMobil</SelectItem>
                    <SelectItem value="Telenor">Telenor</SelectItem>
                    <SelectItem value="Telia">Telia</SelectItem>
                    <SelectItem value="Ice">Ice</SelectItem>
                    <SelectItem value="GlobalConnect">GlobalConnect</SelectItem>
                    <SelectItem value="En annen leverandør">En annen leverandør</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="manedligKostnad" className="text-sm font-bold text-gray-700">
                  Månedlig kostnad <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="manedligKostnad"
                  placeholder="f.eks 5600"
                  value={formData.manedligKostnad}
                  onChange={(e) => handleInputChange("manedligKostnad", e.target.value)}
                  className="bg-white border-orange-200 rounded-xl py-2 focus:border-orange-400 focus:ring-orange-400"
                />
              </div>

              <Button
                onClick={handleNext}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-4 rounded-xl font-bold text-lg shadow-xl"
              >
                Neste
              </Button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-3 pt-2">
              <div className="space-y-1">
                <Label htmlFor="navn" className="text-sm font-bold text-gray-700">
                  Navn <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="navn"
                  value={formData.navn}
                  onChange={(e) => handleInputChange("navn", e.target.value)}
                  className="bg-white border-orange-200 rounded-xl py-2 focus:border-orange-400 focus:ring-orange-400"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="bedrift" className="text-sm font-bold text-gray-700">
                  Bedrift <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="bedrift"
                  placeholder="f.eks Bedrift AS"
                  value={formData.bedrift}
                  onChange={(e) => handleInputChange("bedrift", e.target.value)}
                  className="bg-white border-orange-200 rounded-xl py-2 focus:border-orange-400 focus:ring-orange-400"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="telefonnummer" className="text-sm font-bold text-gray-700">
                  Telefonnummer <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="telefonnummer"
                  value={formData.telefonnummer}
                  onChange={(e) => handleInputChange("telefonnummer", e.target.value)}
                  className="bg-white border-orange-200 rounded-xl py-2 focus:border-orange-400 focus:ring-orange-400"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="epost" className="text-sm font-bold text-gray-700">
                  E-post <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="epost"
                  type="email"
                  value={formData.epost}
                  onChange={(e) => handleInputChange("epost", e.target.value)}
                  className="bg-white border-orange-200 rounded-xl py-2 focus:border-orange-400 focus:ring-orange-400"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="tilleggsinformasjon" className="text-sm font-bold text-gray-700">
                  Tilleggsinformasjon
                </Label>
                <Textarea
                  id="tilleggsinformasjon"
                  placeholder="Hvis du har ønsker eller noe å legge til, kan du gjøre det her. F.eks 150GB data"
                  value={formData.tilleggsinformasjon}
                  onChange={(e) => handleInputChange("tilleggsinformasjon", e.target.value)}
                  className="bg-white border-orange-200 rounded-xl py-2 focus:border-orange-400 focus:ring-orange-400 min-h-[56px]"
                />
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 bg-white border-orange-300 text-orange-700 hover:bg-orange-50 rounded-xl py-2 font-bold"
                >
                  Tilbake
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl py-2 font-bold shadow-xl"
                >
                  Send inn
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Error Popup Modal */}
      <Dialog open={!!errorPopup} onOpenChange={() => setErrorPopup(null)}>
        <DialogContent className="sm:max-w-xs bg-white border-0 rounded-2xl shadow-xl text-center">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-red-600">Skjema mangler informasjon</DialogTitle>
          </DialogHeader>
          {errorPopup && (
            <ul className="text-orange-800 font-semibold text-left my-4 ml-4 list-disc space-y-1">
              {errorPopup.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          )}
          <Button onClick={() => setErrorPopup(null)} className="bg-gradient-to-r from-orange-500 to-red-500 text-white w-full mt-2">Lukk</Button>
        </DialogContent>
      </Dialog>

      {/* Result Popup Modal */}
      <Dialog open={!!resultPopup} onOpenChange={() => setResultPopup(null)}>
        <DialogContent className="sm:max-w-xs bg-white border-0 rounded-2xl shadow-xl text-center">
          <DialogHeader>
            <DialogTitle className={`text-lg font-black ${resultPopup?.success ? 'text-green-700' : 'text-red-600'}`}>{resultPopup?.success ? 'Sendt...' : 'Feilet...'}</DialogTitle>
          </DialogHeader>
          <div className="text-gray-700 font-medium text-base my-4">{resultPopup?.message}</div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
